import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Company, CompanyProject, CompanyProperty, Contact, EntityName } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { absent, nothingUseful, orAbsent, place, propertyType, roleWords, shortDate } from "../lib/format";
import { applySorts, useListParams } from "../lib/useListParams";
import { sortRows } from "../lib/sortRows";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { LookupFacts } from "../components/Enrichment";
import { ActivityTable } from "../components/ActivityTable";
import { CampaignsTable, campaignCount, useRecordCampaigns } from "../components/CampaignsTable";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";
import { ApolloFact } from "../components/CheckApollo";
import { FlagFacts } from "../components/FlagRecord";
import { OtherNamesFact } from "../components/OtherNames";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { EditBar, Fact, FactGroup, Facts, useContestedFacts } from "../ui/Facts";
import { useRecordEdit } from "../lib/useRecordEdit";
import { RecordPage, useRecordNav } from "../ui/RecordPage";
import { Stage, useStageOrder } from "../ui/Stage";
import { FiltersPanel, Toolbar, useFiltersOpen, type Chip } from "../ui/Toolbar";
import { FUNCTION_WORDS, FunctionWord, LEVEL_WORDS, ReachCell, StandingWord, hubspotActivityWord, levelWord, reachScore } from "../ui/words";

/* One company, as a page.
 *
 * Designed from the job rather than from the data model, in the Companies
 * design pass of 2026-10-06 (docs/ux-decisions.md, A record is a page). The
 * question the top of the page answers is whether this company is worth a
 * call and who to call, so the header is the identity and the verdict, with
 * the actions as links beside it and the account's figures under it. The
 * tabs below are at full width: Details first, since 2026-10-08, with the
 * record's facts in groups and every correctable fact corrected in place,
 * then People, because they are what a rep came for, then Properties,
 * Projects and Activity.
 *
 * Every table has search, sort and filter (James, 2026-10-08), through the
 * same toolbar the lists use, with its state in the URL under the table's
 * own prefix so a link carries it. The search asks the server, so it covers
 * the whole set and not the rows loaded so far. The frame is
 * src/ui/RecordPage.tsx, shared with the person and property pages. */

/* "Type" on screen, because that is the word main settled on 2026-10-07 for
   what the lake calls kind. */
const KIND_WORDS: Record<string, string> = {
  operating_company: "Operating company",
  single_property: "One property",
  unclear: "Type unclear",
};

const PAGE = 200;
type Tool = "edit" | "lookup" | "flag" | null;

/** A search term made safe for a PostgREST `or` filter, which reads commas,
 *  parentheses and asterisks as its own syntax. */
const safe = (q: string) => q.replace(/[,()*\\]/g, " ").trim();

export function CompanyPage({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const token = session.idToken;
  const [tool, setTool] = useState<Tool>(null);

  const company = useQuery({
    queryKey: ["company", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("company").select("*").eq("id", id).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Company | null;
    },
  });

  // Every name the record has been known by, for the "also" line.
  const names = useQuery({
    queryKey: ["company-names", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("entity_name")
        .select("name,source,first_seen,seen_count")
        .eq("entity_type", "company").eq("entity_id", id)
        .order("seen_count", { ascending: false }).limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EntityName[];
    },
  });

  const projectCount = useQuery({
    queryKey: ["company-projects", "count", id],
    queryFn: async () => {
      const { error, count } = await apiClient(token)
        .from("company_project").select("project_id", { count: "exact", head: true })
        .eq("company_id", id);
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const openFlags = useQuery({
    queryKey: ["flags-open", "company", id],
    queryFn: async () => {
      const { error, count } = await apiClient(token)
        .from("record_flag").select("id", { count: "exact", head: true })
        .eq("entity_type", "company").eq("entity_id", id).is("resolved_at", null);
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const campaigns = useRecordCampaigns(token, "company", id);

  const c = company.data;
  const otherNames = (names.data ?? []).map((n) => n.name).filter((n): n is string => Boolean(n) && n !== c?.name);
  const hubspot = c?.hubspot_status;

  return (
    <RecordPage
      backLabel="Back to companies" backTo="/companies" fromKey="companies"
      loading={company.isLoading}
      loadedAt={company.dataUpdatedAt} refreshing={company.isFetching} onRefresh={() => company.refetch()}
      error={company.isError ? (company.error as Error).message : null}
      notFound={!c && !company.isLoading ? "No company with that id. It may have been merged." : null}
      banner={c?.do_not_contact ? (
        <p className="banner refusal rp-dnc" role="alert">
          Do not contact{c.do_not_contact_reason ? `: ${c.do_not_contact_reason}` : "."}
        </p>
      ) : null}
      title={c?.name ?? "Company"}
      aka={otherNames}
      facts={c ? (
        <>
          {c.website ? <a href={c.website} target="_blank" rel="noreferrer">{c.website.replace(/^https?:\/\//, "")}</a> : <span className="muted">no website on record</span>}
          {" · "}{place(c.hq_city, c.hq_state)}
        </>
      ) : null}
      badges={c ? (
        <>
          {c.kind ? <Badge tone="plain">{KIND_WORDS[c.kind] ?? c.kind.replace(/_/g, " ")}</Badge> : null}
          {c.relevance ? <Badge tone={c.relevance === "target" ? "ok" : "plain"}>{c.relevance.replace(/_/g, " ")}</Badge> : <Badge tone="quiet" title="The engine has not classified this company yet">Not classified</Badge>}
          {hubspot === "yes" ? <Badge tone="plain">In HubSpot</Badge>
            : hubspot === "not checked" ? <Badge tone="quiet" title="Not checked against HubSpot yet">HubSpot not checked</Badge>
            : <Badge tone="quiet" title="Not found in HubSpot">Not in HubSpot</Badge>}
          {c.hubspot_last_contacted_at ? <Badge tone="ok" title="When Buzze last contacted this company, from HubSpot">Contacted {shortDate(c.hubspot_last_contacted_at)}</Badge> : null}
          {openFlags.data ? <Badge tone="waiting" title="Somebody has said this record is wrong">Flagged ({openFlags.data})</Badge> : null}
        </>
      ) : null}
      actions={[
        { label: "Edit", tab: "details", onSelect: () => setTool("edit") },
        { label: "Run web enrichment", tab: "details", anchor: "lookup", onSelect: () => setTool("lookup") },
        { label: "Run Apollo enrichment", tab: "details", anchor: "apollo" },
        { label: "Flag this record", tab: "details", anchor: "flag", onSelect: () => setTool("flag") },
      ]}
      figures={[
        { n: c?.property_count, label: "properties", tab: "properties" },
        { n: c?.people_count, label: "people", tab: "people" },
        { n: projectCount.data ?? null, label: "projects", tab: "projects", pending: projectCount.isPending },
        { n: campaignCount(campaigns.data), label: "campaigns", tab: "campaigns", pending: campaigns.isPending },
        { n: c?.source_count, label: "sources", tab: "activity" },
      ]}
      defaultTab="details"
      tabs={[
        { key: "details", label: "Details", content: c ? <DetailsTab token={token} id={id} company={c} tool={tool} setTool={setTool} onChanged={() => company.refetch()} /> : null },
        { key: "people", label: "People", count: c?.people_count, content: <PeopleTab token={token} id={id} total={c?.people_count ?? null} /> },
        { key: "properties", label: "Properties", count: c?.property_count, content: <PropertiesTab token={token} id={id} total={c?.property_count ?? null} /> },
        { key: "projects", label: "Projects", count: projectCount.data ?? null, content: <ProjectsTab token={token} id={id} /> },
        { key: "campaigns", label: "Campaigns", count: campaignCount(campaigns.data), content: <CampaignsTable token={token} entityType="company" entityId={id} /> },
        { key: "activity", label: "Activity", count: c?.source_count, content: <ActivityTable token={token} entityType="company" entityId={id} /> },
      ]}
    />
  );
}

/* The record's facts, in three groups: who the company is, where it stands
   with Buzze, and what EVIE's data on it looks like. Edit, among the
   actions, turns the facts the engine lets a person correct
   (api.editable_field: name, website, kind, relevance and its reason, city,
   state, do not contact and its reason) into inputs, with one note and one
   Save. The tools sit with the facts they change and step aside while
   editing: the web lookup and Apollo under what EVIE knows, the flag under
   with Buzze. */
function DetailsTab({ token, id, company: c, tool, setTool, onChanged }: {
  token: string; id: string; company: Company; tool: Tool; setTool: (t: Tool) => void; onChanged: () => void;
}) {
  const editing = tool === "edit";
  const contested = useContestedFacts(token, "company", id);
  const [saved, setSaved] = useState<string[]>([]);
  const edit = useRecordEdit({
    token, entityType: "company", entityId: id,
    values: c as unknown as Record<string, unknown>,
    onSaved: (result) => {
      setSaved(result.fields);
      window.setTimeout(() => setSaved([]), 4000);
      setTool(null);
      onChanged();
    },
  });
  const hubspot = c.hubspot_status;
  const toolProps = (t: Exclude<Tool, null | "edit">) => ({ open: tool === t, onOpenChange: (o: boolean) => setTool(o ? t : null) });
  return (
    <section className="rp-tabbody" aria-label="Details">
      {editing ? (
        <EditBar pending={edit.pending} saving={edit.saving} error={edit.error}
                 note={edit.note} onNote={edit.setNote} onSave={edit.save}
                 onCancel={() => { edit.reset(); setTool(null); }} />
      ) : null}
      <Facts token={token} entityType="company" values={c as unknown as Record<string, unknown>}
             words={{ kind: KIND_WORDS }} editing={editing} draft={edit.draft} onDraft={edit.setField}
             contested={contested.data ?? {}} onUseClaim={edit.applyClaim} saved={saved}>
        <FactGroup title="The company">
          <Fact label="Name" field="name">{c.name}</Fact>
          <Fact label="Website" field="website">
            {c.website ? <a href={c.website} target="_blank" rel="noreferrer">{c.website}</a> : absent()}
          </Fact>
          <Fact label="Type" field="kind">{c.kind ? (KIND_WORDS[c.kind] ?? c.kind.replace(/_/g, " ")) : absent()}</Fact>
          <Fact label="Relevance" field="relevance">{c.relevance ? c.relevance.replace(/_/g, " ") : absent("not classified")}</Fact>
          <Fact label="Why" field="relevance_reason">{orAbsent(c.relevance_reason)}</Fact>
          <Fact label="City" field="hq_city">{orAbsent(c.hq_city)}</Fact>
          <Fact label="State" field="hq_state">{orAbsent(c.hq_state)}</Fact>
          <OtherNamesFact token={token} entityType="company" entityId={id} currentName={c.name} />
        </FactGroup>

        <FactGroup title="With Buzze">
          <Fact label="In HubSpot">
            {hubspot === "yes" ? "yes"
              : hubspot === "not checked" ? <span title="Not checked against HubSpot yet">not checked</span>
              : nothingUseful("Not found in HubSpot")}
          </Fact>
          <Fact label="HubSpot id">{orAbsent(c.hubspot_company_id)}</Fact>
          <Fact label="HubSpot checked">{shortDate(c.hubspot_checked_at)}</Fact>
          {/* From HubSpot's own properties through the crosswalk (engine
              migration 184). Absent means HubSpot has nothing logged or the
              crosswalk has not read it yet, which are told apart by the
              checked date above. */}
          <Fact label="Last contacted">
            {c.hubspot_last_contacted_at
              ? <>{shortDate(c.hubspot_last_contacted_at)}{c.hubspot_contact_count ? <span className="muted"> · {c.hubspot_contact_count.toLocaleString()} time{c.hubspot_contact_count === 1 ? "" : "s"}</span> : null}</>
              : absent("-", "No contact logged in HubSpot, or not read from it yet")}
          </Fact>
          <Fact label="Last activity">
            {c.hubspot_last_activity_at
              ? <>{shortDate(c.hubspot_last_activity_at)}{hubspotActivityWord(c.hubspot_last_activity) ? <span className="muted"> · {hubspotActivityWord(c.hubspot_last_activity)}</span> : null}</>
              : absent("-", "No sales activity logged in HubSpot, or not read from it yet")}
          </Fact>
          <Fact label="HubSpot owner">{orAbsent(c.hubspot_owner)}</Fact>
          <Fact label="Do not contact" field="do_not_contact">{c.do_not_contact ? <span className="refusal">yes</span> : "no"}</Fact>
          <Fact label="Why not" field="do_not_contact_reason">{orAbsent(c.do_not_contact_reason)}</Fact>
          {editing ? null : <FlagFacts token={token} entityType="company" entityId={id} {...toolProps("flag")} />}
        </FactGroup>

        <FactGroup title="What EVIE knows">
          {editing ? null : <LookupFacts token={token} entityType="company" entityId={id} {...toolProps("lookup")} />}
          {editing ? null : <ApolloFact token={token} entityType="company" entityId={id} onFilled={onChanged} />}
          <Fact label="Sources">{c.source_count ?? absent()}</Fact>
          <Fact label="Source tiers">{c.source_tiers?.join(", ") || absent()}</Fact>
          <Fact label="Signals">{c.signal_count ?? absent()}</Fact>
          <Fact label="Newest source">{shortDate(c.newest_source_date)}</Fact>
          <Fact label="First seen">{shortDate(c.created_at)}</Fact>
          <Fact label="Last changed">{shortDate(c.updated_at)}</Fact>
          <Fact label="Id">{c.id}</Fact>
        </FactGroup>
      </Facts>
    </section>
  );
}

/* One row per person. api.contact returns a row per employment, so a person
   with two open rows at one company would appear twice. This keeps the row
   with the most on it. The real fix is where the duplicate employment is
   written (docs/backlog.md), and engine migration 090 tried the view and 091
   reverted it for making api.campaign_run 400 times slower. */
function onePerPerson(rows: Contact[]): Contact[] {
  const worth = (c: Contact) =>
    (c.title?.length ?? 0)
    + (c.level ? 1 : 0) + (c.job_function ? 1 : 0)
    + (c.mobile ? 1 : 0) + (c.email ? 1 : 0)
    + (c.phone ? 1 : 0) + (c.linkedin_url ? 1 : 0);
  const best = new Map<string, Contact>();
  for (const row of rows) {
    const key = row.person_id ?? `${row.full_name}`;
    const held = best.get(key);
    if (!held || worth(row) > worth(held)) best.set(key, row);
  }
  return [...best.values()];
}

async function sourceCounts(token: string, ids: string[]): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    if (!chunk.length) continue;
    const { data, error } = await apiClient(token)
      .from("person").select("id,source_count").in("id", chunk).limit(chunk.length);
    if (error) return out;
    for (const r of (data ?? []) as { id: string; source_count: number | null }[]) {
      out.set(r.id, r.source_count ?? 0);
    }
  }
  return out;
}

const PEOPLE_CSV = ["full_name", "title", "level", "job_function", "employment_standing", "email", "mobile", "phone", "linkedin_url"];
const PEOPLE_FILTERS = ["reach", "level", "fn"] as const;

/* The people at a company, as a table. Reachable first by default, as James
   set on 2026-09-22, then by how many sources know the person, then by name.
   The rows are collapsed to one per person after they arrive, so the sort and
   the filters are applied here to what the server returned. The search asks
   the server. The filters: reach either way, because a rep wants the
   reachable and a researcher wants the rest, level, and function. */
function PeopleTab({ token, id, total }: { token: string; id: string; total: number | null }) {
  const [pages, setPages] = useState(1);
  const params = useListParams("reach", PEOPLE_FILTERS, false, "", "p.");
  const reachF = params.filters["reach"] ?? "";
  const levelF = params.filters["level"] ?? "";
  const fn = params.filters["fn"] ?? "";
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(Boolean(reachF || levelF || fn));
  const q = safe(params.search);

  const people = useQuery({
    queryKey: ["company-people", id, pages, q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const asked = PAGE * pages;
      let query = apiClient(token)
        .from("contact")
        .select("person_id,full_name,title,level,job_function,employment_standing,effective_standing,email,mobile,phone,linkedin_url")
        .eq("parent_type", "company").eq("parent_id", id);
      if (q) query = query.or(`full_name.ilike.*${q}*,title.ilike.*${q}*`);
      const { data, error } = await query
        .order("full_name", { ascending: true }).order("person_id", { ascending: true })
        .limit(asked);
      if (error) throw new Error(apiErrorMessage(error));
      const rows = (data ?? []) as Contact[];
      const one = onePerPerson(rows);
      const ids = one.map((p) => p.person_id).filter(Boolean) as string[];
      return { rows: one, sources: await sourceCounts(token, ids), more: rows.length === asked };
    },
  });

  const sources = people.data?.sources ?? new Map<string, number>();
  const filtered = (people.data?.rows ?? [])
    .filter((p) => !reachF || (reachF === "yes") === (reachScore(p) > 0))
    .filter((p) => !levelF || p.level === levelF)
    .filter((p) => !fn || p.job_function === fn);
  const rows = sortRows(filtered, params.sorts, (p, column) => {
    switch (column) {
      case "name": return p.full_name;
      case "title": return p.title;
      case "level": return levelWord(p.level);
      case "job_function": return FUNCTION_WORDS[p.job_function ?? ""] ?? null;
      case "reach": return reachScore(p);
      case "standing": return p.employment_standing;
      case "sources": return sources.get(p.person_id ?? "") ?? 0;
      default: return null;
    }
  }, [{ column: "sources", ascending: false }, { column: "name", ascending: true }]);
  const reachableCount = (people.data?.rows ?? []).filter((p) => reachScore(p) > 0).length;

  const columns: ColumnSpec<Contact>[] = [
    { id: "name", header: "Name", role: "leading", fields: ["full_name"], sort: "name", title: true, narrowLabel: null,
      cell: (p) => <RecordLink to="people" id={p.person_id} label={p.full_name} /> },
    { id: "title", header: "Title", role: "leading", fields: ["title"], sort: "title", narrowLabel: null,
      cell: (p) => orAbsent(p.title) },
    { id: "level", header: "Level", role: "secondary", fields: ["level"], sort: "level", narrowLabel: null,
      cell: (p) => orAbsent(levelWord(p.level)) },
    { id: "job_function", header: "Function", role: "secondary", fields: ["job_function"], sort: "job_function", narrowLabel: null,
      cell: (p) => <FunctionWord fn={p.job_function} /> },
    { id: "reach", header: "Reach", role: "secondary", fields: ["mobile"], sort: "reach", sortDescendingFirst: true, narrowLabel: null,
      cell: (p) => <ReachCell p={p} /> },
    { id: "standing", header: "Standing", role: "secondary", fields: ["employment_standing"], sort: "standing", narrowLabel: null,
      cell: (p) => <StandingWord standing={p.employment_standing} /> },
    { id: "sources", header: "Sources", role: "detail", fields: [], align: "num", sort: "sources",
      cell: (p) => (sources.get(p.person_id ?? "") ?? 0).toLocaleString() },
  ];

  const setFilter = (k: string, v: string) => params.set({ filters: { ...params.filters, [k]: v } });
  const chips: Chip[] = [
    ...(reachF ? [{ label: reachF === "yes" ? "Reachable" : "Not reachable", onRemove: () => setFilter("reach", "") }] : []),
    ...(levelF ? [{ label: `Level: ${levelWord(levelF)}`, onRemove: () => setFilter("level", "") }] : []),
    ...(fn ? [{ label: `Function: ${FUNCTION_WORDS[fn] ?? fn}`, onRemove: () => setFilter("fn", "") }] : []),
  ];

  return (
    <section className="rp-tabbody" aria-label="People">
      <Toolbar
        id="people"
        search={{ label: "Search by name or title", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {} })}
        right={people.data?.rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("company-people"),
            toCsv(PEOPLE_CSV, rows as unknown as Record<string, unknown>[]))}>
            CSV
          </Button>
        ) : null}
      />
      <FiltersPanel id="people" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          <NativeSelect
            label="Reach" value={reachF} onChange={(e) => setFilter("reach", e.currentTarget.value)}
            data={[{ value: "", label: "any" }, { value: "yes", label: "reachable" }, { value: "no", label: "not reachable" }]}
          />
          <NativeSelect
            label="Level" value={levelF} onChange={(e) => setFilter("level", e.currentTarget.value)}
            data={[{ value: "", label: "any" }, ...Object.entries(LEVEL_WORDS).map(([value, label]) => ({ value, label }))]}
          />
          <NativeSelect
            label="Function" value={fn} onChange={(e) => setFilter("fn", e.currentTarget.value)}
            data={[{ value: "", label: "any" }, ...Object.entries(FUNCTION_WORDS).map(([value, label]) => ({ value, label }))]}
          />
        </div>
      </FiltersPanel>
      <p className="muted rp-count">
        {total === null ? "" : `${total.toLocaleString()} people on record. `}
        {people.data ? `${reachableCount.toLocaleString()} reachable.` : null}
        {q ? ` Showing matches for "${params.search.trim()}".` : ""}
      </p>
      {people.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : people.isError ? <p className="refusal">{(people.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {chips.length || q
              ? "No people match these filters."
              : <>No people on record. Run Apollo enrichment, on the <Link to="?tab=details">Details tab</Link>, adds contacts in buying roles.</>}
          </p>
        )
        : <DataTable columns={columns} rows={rows} rowKey={(p) => p.person_id ?? p.full_name ?? ""} params={params} />}
      {people.data?.more ? (
        <p className="muted">
          Showing {people.data.rows.length.toLocaleString()}.{" "}
          <Button size="compact-sm" onClick={() => setPages((n) => n + 1)} disabled={people.isFetching}>
            {people.isFetching ? "Loading" : "Show more"}
          </Button>
        </p>
      ) : null}
    </section>
  );
}

const PROPERTIES_CSV = ["property_name", "property_address_line1", "property_city", "property_state", "property_type", "unit_count", "roles", "standing", "source_count"];
const PROPERTY_FILTERS = ["state", "type"] as const;

function PropertiesTab({ token, id, total }: { token: string; id: string; total: number | null }) {
  const [pages, setPages] = useState(1);
  // By sources, the order James set for buildings on 2026-10-08, on
  // api.company_property.source_count (engine migration 182).
  const params = useListParams("source_count", PROPERTY_FILTERS, false, "", "pr.");
  const state = params.filters["state"] ?? "";
  const type = params.filters["type"] ?? "";
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(Boolean(state || type));
  const nav = useRecordNav();
  const q = safe(params.search);

  const properties = useQuery({
    queryKey: ["company-properties", id, pages, params.sorts, q, state, type],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let query = apiClient(token)
        .from("company_property")
        .select("property_id,property_name,property_address_line1,property_city,property_state,property_type,unit_count,roles,standing,source_count")
        .eq("company_id", id).neq("standing", "ended");
      if (q) query = query.or(`property_name.ilike.*${q}*,property_address_line1.ilike.*${q}*,property_city.ilike.*${q}*`);
      if (state) query = query.eq("property_state", state);
      if (type) query = query.ilike("property_type", `*${safe(type)}*`);
      const { data, error } = await applySorts(query, params.sorts)
        .order("property_id", { ascending: true })
        .limit(PAGE * pages);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CompanyProperty[];
    },
  });

  const columns: ColumnSpec<CompanyProperty>[] = [
    { id: "property", header: "Property", role: "leading", fields: ["property_name"], sort: "property_name", title: true, narrowLabel: null,
      cell: (p) => <RecordLink to="properties" id={p.property_id} label={p.property_name || p.property_address_line1} /> },
    { id: "city", header: "City", role: "leading", fields: ["property_city"], sort: "property_city", narrowLabel: null, cell: (p) => orAbsent(p.property_city) },
    { id: "state", header: "State", role: "leading", fields: ["property_state"], sort: "property_state", narrowLabel: null, cell: (p) => orAbsent(p.property_state) },
    { id: "type", header: "Type", role: "secondary", fields: ["property_type"], sort: "property_type", narrowLabel: null, cell: (p) => propertyType(p.property_type) },
    { id: "units", header: "Units", role: "secondary", fields: ["unit_count"], align: "num", sort: "unit_count",
      cell: (p) => p.unit_count ?? absent() },
    { id: "role", header: "Role", role: "secondary", fields: ["roles"], narrowLabel: null, cell: (p) => roleWords(p.roles) || absent() },
    { id: "sources", header: "Sources", role: "secondary", fields: ["source_count"], align: "num", sort: "source_count",
      cell: (p) => p.source_count ?? absent() },
    { id: "standing", header: "Standing", role: "detail", fields: ["standing"], sort: "standing",
      cell: (p) => <StandingWord standing={p.standing} /> },
  ];

  const setFilter = (k: string, v: string) => params.set({ filters: { ...params.filters, [k]: v } });
  const chips: Chip[] = [
    ...(state ? [{ label: `State: ${state}`, onRemove: () => setFilter("state", "") }] : []),
    ...(type ? [{ label: `Type: ${type}`, onRemove: () => setFilter("type", "") }] : []),
  ];
  const rows = properties.data ?? [];
  const narrowed = chips.length > 0 || Boolean(q);
  return (
    <section className="rp-tabbody" aria-label="Properties">
      <Toolbar
        id="properties"
        search={{ label: "Search by name, address or city", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {} })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("company-properties"),
            toCsv(PROPERTIES_CSV, rows.map((p) => ({ ...p, roles: roleWords(p.roles) }))))}>
            CSV
          </Button>
        ) : null}
      />
      <FiltersPanel id="properties" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          {/* Set on blur or Enter rather than on each keystroke, so "CA"
              asks the server once. */}
          <TextInput label="State" description="Two-letter code" w="6rem" defaultValue={state} key={`s-${state}`}
                     onBlur={(e) => setFilter("state", e.currentTarget.value.toUpperCase().slice(0, 2))}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFilter("state", e.currentTarget.value.toUpperCase().slice(0, 2)); } }} />
          <TextInput label="Type" w="12rem" defaultValue={type} key={`t-${type}`}
                     onBlur={(e) => setFilter("type", e.currentTarget.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFilter("type", e.currentTarget.value); } }} />
        </div>
      </FiltersPanel>
      <p className="muted rp-count">
        {total === null ? "" : `${total.toLocaleString()} properties with a current or unconfirmed role.`}
        {narrowed ? ` Showing ${rows.length.toLocaleString()} that match.` : ""}
      </p>
      {properties.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : properties.isError ? <p className="refusal">{(properties.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {narrowed
              ? "No properties match these filters."
              : <>No properties on record. A property reaches a company through a permit, a filing or a page the engine reads, and <Link to="?tab=details">Run web enrichment</Link> asks for a search.</>}
          </p>
        )
        : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(p) => p.property_id ?? ""}
            params={params}
            openId={nav?.lastOpened}
            onOpen={(p) => { if (p.property_id) nav?.open(`/properties/${p.property_id}`, p.property_id); }}
          />
        )}
      {!narrowed && total !== null && rows.length < total ? (
        <p className="muted">
          Showing {rows.length.toLocaleString()} of {total.toLocaleString()}.{" "}
          <Button size="compact-sm" onClick={() => setPages((n) => n + 1)} disabled={properties.isFetching}>
            {properties.isFetching ? "Loading" : "Show more"}
          </Button>
        </p>
      ) : null}
    </section>
  );
}

const PROJECT_FILTERS = ["stage"] as const;

function ProjectsTab({ token, id }: { token: string; id: string }) {
  const params = useListParams("newest_source_date", PROJECT_FILTERS, false, "", "pj.");
  const stage = params.filters["stage"] ?? "";
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(Boolean(stage));
  const nav = useRecordNav();
  const { words, orderOf } = useStageOrder(token);
  const q = safe(params.search);

  const projects = useQuery({
    queryKey: ["company-projects", id, params.sorts, q, stage],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let query = apiClient(token)
        .from("company_project")
        .select("project_id,name,kind,status,stage_word,unit_count,est_completion,newest_source_date,property_id,property_name,property_city,property_state")
        .eq("company_id", id);
      if (q) query = query.or(`name.ilike.*${q}*,property_name.ilike.*${q}*`);
      if (stage) query = query.eq("stage_word", stage);
      const { data, error } = await applySorts(query, params.sorts)
        .order("project_id", { ascending: true })
        .limit(PAGE);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CompanyProject[];
    },
  });

  const columns: ColumnSpec<CompanyProject>[] = [
    /* A project has no page of its own (James, 2026-09-10), so its name
       opens the building it is on, the same place the row opens. */
    { id: "name", header: "Project", role: "leading", fields: ["name"], sort: "name", title: true, narrowLabel: null,
      cell: (p) => <RecordLink to="properties" id={p.property_id} label={p.name} /> },
    { id: "stage", header: "Stage", role: "leading", fields: ["stage_word"], sort: "stage_word", narrowLabel: null,
      cell: (p) => <Stage word={p.stage_word} order={orderOf(p.stage_word)} /> },
    { id: "units", header: "Units", role: "secondary", fields: ["unit_count"], align: "num", sort: "unit_count",
      cell: (p) => p.unit_count ?? absent() },
    { id: "property", header: "Property", role: "secondary", fields: ["property_name"], sort: "property_name", narrowLabel: "at",
      cell: (p) => <RecordLink to="properties" id={p.property_id} label={p.property_name} /> },
    { id: "city", header: "City", role: "secondary", fields: ["property_city"], sort: "property_city", narrowLabel: null, cell: (p) => orAbsent(p.property_city) },
    { id: "state", header: "State", role: "secondary", fields: ["property_state"], sort: "property_state", narrowLabel: null, cell: (p) => orAbsent(p.property_state) },
    { id: "completion", header: "Expected finish", role: "secondary", fields: ["est_completion"], sort: "est_completion", sortDescendingFirst: true,
      cell: (p) => shortDate(p.est_completion) },
    { id: "newest", header: "Newest source", role: "detail", fields: ["newest_source_date"], sort: "newest_source_date", sortDescendingFirst: true,
      cell: (p) => shortDate(p.newest_source_date) },
  ];

  const chips: Chip[] = stage ? [{ label: `Stage: ${stage}`, onRemove: () => params.set({ filters: {} }) }] : [];
  const rows = projects.data ?? [];
  const narrowed = chips.length > 0 || Boolean(q);
  return (
    <section className="rp-tabbody" aria-label="Projects">
      <Toolbar
        id="projects"
        search={{ label: "Search by project or property", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {} })}
      />
      <FiltersPanel id="projects" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          <NativeSelect
            label="Stage" value={stage} onChange={(e) => params.set({ filters: { stage: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, ...words.map((w) => ({ value: w.word, label: w.word }))]}
          />
        </div>
      </FiltersPanel>
      <p className="muted rp-count">
        Projects on this company's properties.
        {narrowed ? ` Showing ${rows.length.toLocaleString()} that match.` : ""}
      </p>
      {projects.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : projects.isError ? <p className="refusal">{(projects.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {narrowed
              ? "No projects match these filters."
              : <>No projects on this company's properties. A project arrives from a permit, a filing or a news page the engine reads, and <Link to="?tab=details">Run web enrichment</Link> asks for a search.</>}
          </p>
        )
        : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(p) => p.project_id ?? ""}
            params={params}
            openId={nav?.lastOpened}
            onOpen={(p) => { if (p.property_id) nav?.open(`/properties/${p.property_id}`, p.project_id); }}
          />
        )}
    </section>
  );
}
